import { Box, getVariant } from '@apia/theme';
import { IConfirm } from '../../dialogs';
import { MouseEvent, useCallback } from 'react';
import { DialogButtonBar } from './DialogButtonBar';
import { SimpleButton } from '../../forms/buttons/SimpleButton';
import { useModalContext } from '../ModalContext';
import { getLabel } from '@apia/util';

export const Confirm = ({
  children,
  additionalButtons,
  additionalButtonsOnRight,
  cancelButtonText: cancelText,
  className,
  confirmButtonText: confirmText,
  confirmButtonVariant = 'primary',
  contentRef,
  disabled,
  hideCancelButton,
  hideConfirmButton,
  isLoading,
  onCancel,
  onConfirm,
  variant,
  htmlContent,
}: Omit<IConfirm, 'title'>) => {
  const { descriptionId } = useModalContext();

  const showsCancelButton = onCancel && !hideCancelButton;
  const showsConfirmButton = !hideConfirmButton;

  if (!showsCancelButton && showsConfirmButton) {
    confirmText = confirmText || getLabel('btnClose').text;
  } else if (showsConfirmButton) {
    confirmText = confirmText || getLabel('btnCon').text;
    cancelText = cancelText || getLabel('btnCan').text;
  }

  return (
    <Box
      {...getVariant(variant ?? 'layout.common.modals.confirm')}
      className={`${className ?? ''} confirm`}
    >
      <Box ref={contentRef} className="confirm__content" id={descriptionId}>
        {htmlContent ? (
          <Box dangerouslySetInnerHTML={{ __html: htmlContent }} />
        ) : (
          children
        )}
      </Box>
      <DialogButtonBar className="confirm__buttonBar">
        <Box
          className="confirm__customButtons"
          onClick={useCallback(
            (ev: MouseEvent) => {
              if ((ev.target as HTMLElement).closest('button')) onCancel?.();
            },
            [onCancel],
          )}
        >
          {additionalButtons}
        </Box>

        <Box className="confirm__defaultButtons">
          <Box
            className="confirm__customButtonsOnRight"
            onClick={useCallback(
              (ev: MouseEvent) => {
                if ((ev.target as HTMLElement).closest('button')) onCancel?.();
              },
              [onCancel],
            )}
          >
            {additionalButtonsOnRight}
          </Box>
          {showsConfirmButton && (
            <SimpleButton
              onClick={onConfirm}
              title={confirmText}
              variant={
                !showsCancelButton && showsConfirmButton
                  ? 'outline'
                  : confirmButtonVariant
              }
              disabled={isLoading ?? disabled}
              isLoading={isLoading}
              className="confirm__confirmButton"
              type="submit"
              data-test-id="confirm"
            >
              {confirmText}
            </SimpleButton>
          )}
          {showsCancelButton && (
            <SimpleButton
              onClick={onCancel}
              title={cancelText}
              {...getVariant('secondary')}
              disabled={isLoading}
              className="confirm__cancelButton"
              data-test-id="cancel"
            >
              {cancelText}
            </SimpleButton>
          )}
        </Box>
      </DialogButtonBar>
    </Box>
  );
};
